"use client";

import React from "react";
import Image from "next/image";
import { Check, Sparkles, ArrowRight, ShieldCheck, Play, Award, CheckCircle2, Flame } from "lucide-react";
import { ProcedureSectionData } from "@/lib/procedure-sections/types";
import { publicUrl } from "@/lib/content/path";
import { SafeHtml } from "@/components/shared/SafeHtml";

function ColoredTitle({ text }: { text?: string }) {
  if (!text) return null;
  const words = text.split(" ");
  if (words.length <= 1) return <span>{text}</span>;
  
  let orangeCount = 1;
  if (words.length >= 6) orangeCount = 3;
  else if (words.length >= 4) orangeCount = 2;

  const firstPart = words.slice(0, words.length - orangeCount).join(" ");
  const lastPart = words.slice(words.length - orangeCount).join(" ");

  return (
    <>
      <span className="text-brand-black">{firstPart} </span>
      <span className="text-brand-orange">{lastPart}</span>
    </>
  );
}

function getEmbedUrl(url: string) {
  if (!url) return "";
  const youtubeMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/i);
  if (youtubeMatch) {
    return `https://www.youtube-nocookie.com/embed/${youtubeMatch[1]}`;
  }
  return url;
}

export function SplitMediaSection({ section, index }: { section: ProcedureSectionData; index: number }) {
  const variant = section.layoutVariant || (section.imagePosition === "left" ? "split_standard_left" : "split_standard_right");
  const isImageLeft = variant === "split_standard_left";
  const embedUrl = section.videoUrl ? getEmbedUrl(section.videoUrl) : "";
  const isTopAligned = section.imageAlignment !== "center";
  const isSticky = section.imageSticky !== false;

  // 1. FULL-BLEED LUXURY BANNER OVERLAY VARIANT
  if (variant === "split_banner_overlay") {
    return (
      <div className="w-full">
        <div className="relative overflow-hidden rounded-3xl border border-brand-gray/20 bg-[#0f1115] text-white shadow-2xl">
          {section.image && (
            <div className="absolute inset-0 z-0">
              <Image
                src={publicUrl(section.image)}
                alt={section.imageAlt || section.title}
                fill
                unoptimized
                className="object-cover opacity-60 filter brightness-105"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0f1115]/95 via-[#0f1115]/80 to-transparent" />
            </div>
          )}
          <div className="relative z-10 p-8 sm:p-12 lg:p-16 max-w-2xl">
            {section.badge && (
              <div className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-white shadow-md">
                <Sparkles className="h-3.5 w-3.5" />
                <span>{section.badge}</span>
              </div>
            )}
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-white">
              {section.title}
            </h2>
            {section.subtitle && (
              <p className="mt-3 text-lg text-white/85 leading-relaxed">{section.subtitle}</p>
            )}
            {section.contentHtml && (
              <div className="mt-6 prose prose-invert max-w-none text-white/90 text-base sm:text-lg cms-content">
                <SafeHtml html={section.contentHtml} />
              </div>
            )}
            {section.items && section.items.length > 0 && (
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {section.items.map((item, itemIdx) => (
                  <div key={itemIdx} className="rounded-2xl border border-white/20 bg-white/10 backdrop-blur-md p-4 shadow-sm">
                    <div className="flex items-center gap-2 font-bold text-white text-base">
                      <Sparkles className="h-4 w-4 text-brand-orange" />
                      <span>{item.title}</span>
                    </div>
                    {item.description && (
                      <p className="mt-1 text-sm text-white/80 leading-relaxed">{item.description}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // 2. CLINICAL STAT & METRIC MATRIX VARIANT
  if (variant === "split_stat_cards") {
    return (
      <div className="w-full">
        <div className={`grid grid-cols-1 gap-8 lg:gap-12 lg:grid-cols-12 ${isTopAligned ? "items-start" : "items-center"}`}>
          <div className="lg:col-span-6">
            {section.badge && (
              <span className="font-accent text-xs uppercase tracking-wider text-brand-orange-deep font-semibold">
                {section.badge}
              </span>
            )}
            <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-black leading-tight">
              <ColoredTitle text={section.title} />
            </h2>
            <div className="mt-4 h-1 w-20 rounded-full bg-brand-orange" />
            {section.subtitle && (
              <p className="mt-4 text-lg font-medium text-brand-black/80">{section.subtitle}</p>
            )}
            {section.contentHtml && (
              <div className="mt-6 prose prose-lg max-w-none text-brand-gray font-normal leading-relaxed text-base sm:text-lg cms-content">
                <SafeHtml html={section.contentHtml} />
              </div>
            )}
          </div>

          {/* 2x2 Grid of Highlight / Stat Cards */}
          <div className={`lg:col-span-6 ${isTopAligned ? "self-start" : ""} ${isSticky ? "lg:sticky lg:top-32" : ""}`}>
            {section.items && section.items.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {section.items.map((item, itemIdx) => (
                  <div 
                    key={itemIdx} 
                    className="rounded-3xl border border-brand-gray/15 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-brand-orange/40 hover:shadow-xl flex flex-col justify-between"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-peach/40 text-brand-orange mb-5">
                      <ShieldCheck className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-brand-black mb-2">{item.title}</h3>
                      <p className="text-sm text-brand-gray leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // 3. SIDE-BY-SIDE COMPARISON VARIANT
  if (variant === "split_side_comparison") {
    return (
      <div className="w-full">
        <div className="text-center max-w-3xl mx-auto mb-8 lg:mb-12">
          {section.badge && (
            <span className="font-accent text-xs uppercase tracking-wider text-brand-orange-deep font-semibold">
              {section.badge}
            </span>
          )}
          <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-black leading-tight">
            <ColoredTitle text={section.title} />
          </h2>
          {section.subtitle && (
            <p className="mt-3 text-lg text-brand-gray">{section.subtitle}</p>
          )}
        </div>

        {section.items && section.items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {section.items.slice(0, 2).map((item, itemIdx) => {
              const isHighlight = itemIdx === 0;
              return (
                <div
                  key={itemIdx}
                  className={`rounded-3xl p-8 sm:p-10 border transition-all duration-300 ${
                    isHighlight 
                      ? "border-brand-orange bg-gradient-to-br from-[#FFF8F3] via-white to-brand-peach/20 shadow-xl relative ring-4 ring-brand-orange/10" 
                      : "border-brand-gray/20 bg-white shadow-sm"
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className={`rounded-full px-3.5 py-1 text-xs font-bold uppercase tracking-wider ${
                      isHighlight ? "bg-brand-orange text-white" : "bg-brand-gray-light text-brand-gray"
                    }`}>
                      {isHighlight ? "Dermalife Protocol" : "Conventional Method"}
                    </span>
                    {isHighlight && (
                      <Sparkles className="h-5 w-5 text-brand-orange" />
                    )}
                  </div>
                  <h3 className="text-2xl font-bold text-brand-black mb-3">{item.title}</h3>
                  <div 
                    className="text-base text-brand-gray leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: item.description }}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // 4. CLINICAL PILLAR STACK VARIANT
  if (variant === "split_clinical_highlights") {
    return (
      <div className="w-full">
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 ${isTopAligned ? "items-start" : "items-center"}`}>
          <div className={`lg:col-span-5 ${isTopAligned ? "self-start" : ""} ${isSticky ? "lg:sticky lg:top-32" : ""}`}>
            {section.badge && (
              <span className="font-accent text-xs uppercase tracking-wider text-brand-orange-deep font-semibold">
                {section.badge}
              </span>
            )}
            <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-black leading-tight">
              <ColoredTitle text={section.title} />
            </h2>
            <div className="mt-4 h-1 w-20 rounded-full bg-brand-orange" />
            {section.subtitle && (
              <p className="mt-4 text-lg text-brand-gray font-medium leading-relaxed">{section.subtitle}</p>
            )}
            {section.contentHtml && (
              <div className="mt-6 prose prose-lg max-w-none text-brand-gray font-normal leading-relaxed text-base sm:text-lg cms-content">
                <SafeHtml html={section.contentHtml} />
              </div>
            )}
          </div>

          <div className="lg:col-span-7 space-y-4">
            {section.items && section.items.map((item, itemIdx) => (
              <div
                key={itemIdx}
                className="group relative overflow-hidden rounded-3xl border border-brand-gray/15 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-brand-orange/50 hover:shadow-xl flex items-start gap-5"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-peach/40 text-brand-orange font-mono font-bold text-lg group-hover:bg-brand-orange group-hover:text-white transition-colors">
                  0{itemIdx + 1}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-xl font-bold text-brand-black group-hover:text-brand-orange-deep transition-colors">
                    {item.title}
                  </h3>
                  {item.description && (
                    <div 
                      className="mt-2 text-sm sm:text-base text-brand-gray leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: item.description }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // 5. CINEMATIC VIDEO SPOTLIGHT VARIANT
  if (variant === "split_video_spotlight" && (section.videoUrl || embedUrl)) {
    return (
      <div className="w-full">
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 ${isTopAligned ? "items-start" : "items-center"}`}>
          <div className="lg:col-span-6 order-2 lg:order-1">
            {section.badge && (
              <span className="font-accent text-xs uppercase tracking-wider text-brand-orange-deep font-semibold">
                {section.badge}
              </span>
            )}
            <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-black leading-tight">
              <ColoredTitle text={section.title} />
            </h2>
            <div className="mt-4 h-1 w-20 rounded-full bg-brand-orange" />
            {section.subtitle && (
              <p className="mt-4 text-lg text-brand-gray font-medium leading-relaxed">{section.subtitle}</p>
            )}
            {section.contentHtml && (
              <div className="mt-6 prose prose-lg max-w-none text-brand-gray font-normal leading-relaxed text-base sm:text-lg cms-content">
                <SafeHtml html={section.contentHtml} />
              </div>
            )}
            {section.items && section.items.length > 0 && (
              <div className="mt-6 space-y-3">
                {section.items.map((item, itemIdx) => (
                  <div key={itemIdx} className="flex items-start gap-3 text-sm sm:text-base text-brand-gray">
                    <Check className="h-5 w-5 shrink-0 text-brand-orange mt-0.5" />
                    <div>
                      <strong className="text-brand-black">{item.title}:</strong> {item.description}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={`lg:col-span-6 order-1 lg:order-2 ${isTopAligned ? "self-start" : ""} ${isSticky ? "lg:sticky lg:top-32" : ""}`}>
            <div className="relative aspect-video w-full overflow-hidden rounded-3xl border border-brand-gray/20 bg-brand-black shadow-2xl ring-4 ring-brand-orange/10">
              <iframe
                src={embedUrl}
                title={section.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="h-full w-full border-0"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 6 & 7. STANDARD SPLIT (IMAGE RIGHT OR LEFT) WITH FLOATING CLINICAL BADGE
  return (
    <div className="w-full">
      <div className={`grid grid-cols-1 gap-8 lg:gap-12 lg:grid-cols-12 ${isTopAligned ? "items-start" : "items-center"}`}>
        {/* Text Column */}
        <div className={`lg:col-span-6 ${isImageLeft ? "order-2 lg:order-2" : "order-2 lg:order-1"}`}>
          {section.badge && (
            <span className="font-accent text-xs uppercase tracking-wider text-brand-orange-deep font-semibold">
              {section.badge}
            </span>
          )}
          <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-black leading-tight">
            <ColoredTitle text={section.title} />
          </h2>
          <div className="mt-4 h-1 w-20 rounded-full bg-brand-orange" />
          {section.subtitle && (
            <p className="mt-4 text-lg text-brand-gray font-medium leading-relaxed">{section.subtitle}</p>
          )}
          {section.contentHtml && (
            <div className="mt-6 prose prose-lg max-w-none text-brand-gray font-normal leading-relaxed text-base sm:text-lg cms-content">
              <SafeHtml html={section.contentHtml} />
            </div>
          )}
          {section.items && section.items.length > 0 && (
            <div className="mt-8 space-y-4">
              {section.items.map((item, itemIdx) => (
                <div key={itemIdx} className="flex items-start gap-4 rounded-2xl border border-brand-gray/10 bg-white p-4 shadow-2xs hover:border-brand-orange/30 transition-colors">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-peach/40 text-brand-orange mt-0.5">
                    <Check className="h-4 w-4 stroke-[2.5]" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-brand-black">{item.title}</h3>
                    {item.description && (
                      <p className="mt-1 text-sm text-brand-gray leading-relaxed">{item.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Media Column with Clinical Quality Badge */}
        <div className={`lg:col-span-6 ${isImageLeft ? "order-1 lg:order-1" : "order-1 lg:order-2"} ${isTopAligned ? "self-start" : ""} ${isSticky ? "lg:sticky lg:top-32" : ""}`}>
          <div className="relative mx-auto max-w-lg lg:max-w-none">
            {section.image ? (
              <div className="relative aspect-[4/3] w-full overflow-hidden rounded-3xl border border-brand-gray/20 bg-white shadow-xl">
                <Image
                  src={publicUrl(section.image)}
                  alt={section.imageAlt || section.title}
                  fill
                  unoptimized
                  className="object-cover transition-transform duration-500 hover:scale-[1.02]"
                />
              </div>
            ) : (
              <div className="aspect-[4/3] w-full rounded-3xl bg-gradient-to-br from-brand-peach/40 via-white to-brand-peach/20 border border-brand-orange/20 flex items-center justify-center p-8">
                <Sparkles className="h-16 w-16 text-brand-orange" />
              </div>
            )}

            {/* Clinical Precision Caption Tag (placed cleanly below image so it does not obscure diagram) */}
            <div className="mt-3 flex items-center justify-between rounded-2xl border border-brand-gray/15 bg-white/90 px-4 py-2.5 shadow-xs backdrop-blur-md">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-orange to-brand-orange-deep text-white shadow-xs">
                  <Award className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-brand-orange-deep">Clinical Precision</p>
                  <p className="text-xs font-bold text-brand-black">Dermalife Standard Protocols</p>
                </div>
              </div>
              <span className="text-[11px] font-medium text-brand-gray hidden sm:inline-block">Medical Reference</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
